package com.iptvplayer.web.ui

import androidx.compose.runtime.*
import com.iptvplayer.shared.domain.model.ContentType
import com.iptvplayer.shared.presentation.viewmodel.*
import com.iptvplayer.web.ui.screen.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/** Navigate-to-player callback: (contentId, name, streamUrl, type, logoUrl). */
typealias OnPlay = (String, String, String, ContentType, String?) -> Unit

/** Navigate-to-series-detail callback: (sourceId, seriesId, seriesName). */
typealias OnSeriesOpen = (String, Int, String) -> Unit

/**
 * Navigate-to-player for a series episode:
 * (episodeId, name, streamUrl, logoUrl, seriesSourceId, seriesId).
 * The series context lets the player auto-advance to the next episode.
 */
typealias OnPlayEpisode = (String, String, String, String?, String, Int) -> Unit

sealed interface Route {
    data object Home : Route
    data object Browse : Route
    data object Favorites : Route
    data object History : Route
    data object Settings : Route
    data object Sources : Route
    data object Devices : Route
    data object Admin : Route
    data class SeriesDetail(val sourceId: String, val seriesId: Int, val name: String) : Route
    data class Player(
        val contentId: String,
        val name: String,
        val url: String,
        val type: ContentType,
        val logo: String?,
        val watchRoomId: String? = null, // non-null = joined a Watch Together room as a guest
        // Series context of the playing episode — enables auto-advance to the next one.
        val seriesSourceId: String? = null,
        val seriesId: Int? = null,
    ) : Route
}

@Composable
fun App() {
    val authVm = remember { koinGet<AuthViewModel>() }
    val authState by authVm.state.collectAsState()

    // Watch Together deep link (#/watch/<id>): join as a guest and go straight to
    // the player, bypassing login (anyone with the link can join).
    var watchRoute by remember { mutableStateOf<Route.Player?>(null) }
    var watchStatus by remember { mutableStateOf<String?>(null) }
    LaunchedEffect(Unit) {
        val match = Regex("#/watch/([A-Za-z0-9]+)").find(kotlinx.browser.window.location.hash)
        if (match != null) {
            watchStatus = "Joining watch party…"
            WatchApi.joinRoom(
                match.groupValues[1],
                onOk = { data ->
                    WebSession.token = data.guest_token as String
                    val c = data.content
                    val type = if ((c.type as? String) == "CHANNEL") ContentType.CHANNEL else ContentType.VOD
                    watchStatus = null
                    watchRoute = Route.Player(
                        contentId = c.content_id as String,
                        name = (c.name as? String) ?: "Watch Party",
                        url = c.stream_url as String,
                        type = type,
                        logo = c.logo as? String,
                        watchRoomId = match.groupValues[1],
                    )
                },
                onErr = { watchStatus = "Couldn't join: this watch party may have ended." },
            )
        }
    }

    val wr = watchRoute
    if (wr != null) {
        PlayerScreen(wr, onBack = {
            watchRoute = null
            kotlinx.browser.window.location.hash = ""
        })
        return
    }
    watchStatus?.let { msg ->
        Div(attrs = { style { property("padding", "40px"); property("text-align", "center"); property("color", Theme.textDim) } }) { Text(msg) }
        return
    }

    if (!authState.isLoggedIn) {
        var authMode by remember { mutableStateOf("landing") }
        when (authMode) {
            "login" -> LoginScreen(authVm, onGoRegister = { authMode = "register" }, onBack = { authMode = "landing" })
            "register" -> RegisterScreen(authVm, onBackToLogin = { authMode = "login" }, onBack = { authMode = "landing" })
            else -> LandingScreen(onSignIn = { authMode = "login" }, onGetStarted = { authMode = "register" })
        }
    } else {
        MainShell(authVm)
    }
}

@Composable
private fun MainShell(authVm: AuthViewModel) {
    val authState by authVm.state.collectAsState()
    var route by remember { mutableStateOf<Route>(Route.Home) }
    var beforePlayer by remember { mutableStateOf<Route>(Route.Home) }

    // Session-scoped view models (created once, reused across navigation).
    val homeVm = remember { koinGet<HomeViewModel>() }
    val browseVm = remember { koinGet<BrowsePagingViewModel>() }
    val favVm = remember { koinGet<FavoritesViewModel>() }
    val histVm = remember { koinGet<HistoryViewModel>() }
    val sourceVm = remember { koinGet<SourceViewModel>() }
    val deviceVm = remember { koinGet<DeviceViewModel>() }

    // Make the access token available for proxying http images/streams.
    LaunchedEffect(Unit) {
        WebSession.token = koinGet<com.iptvplayer.shared.data.local.preferences.TokenStorage>()
            .getToken()?.accessToken
    }
    // Display name for Watch Together chat/voice.
    LaunchedEffect(authState.user?.username) {
        authState.user?.username?.let { WebSession.userName = it }
    }

    val onPlay: OnPlay = { id, name, url, type, logo ->
        beforePlayer = (route as? Route.Player)?.let { Route.Home } ?: route
        route = Route.Player(id, name, url, type, logo)
    }

    val onSeriesOpen: OnSeriesOpen = { sourceId, seriesId, name ->
        route = Route.SeriesDetail(sourceId, seriesId, name)
    }

    val onPlayEpisode: OnPlayEpisode = { id, name, url, logo, seriesSourceId, seriesId ->
        beforePlayer = (route as? Route.Player)?.let { Route.Home } ?: route
        route = Route.Player(
            id, name, url, ContentType.VOD, logo,
            seriesSourceId = seriesSourceId, seriesId = seriesId,
        )
    }

    val current = route
    if (current is Route.Player) {
        PlayerScreen(current, onBack = { route = beforePlayer })
        return
    }

    Div(attrs = { style { minHeight(100.vh); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column) } }) {
        // Top nav
        Div(attrs = {
            style {
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.SpaceBetween)
                property("padding", "12px 22px"); property("border-bottom", "1px solid ${Theme.border}")
                property("background", Theme.surface); position(Position.Sticky); top(0.px); property("z-index", "5")
                property("gap", "16px"); property("flex-wrap", "wrap")
            }
        }) {
            Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); property("gap", "18px") } }) {
                Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); property("gap", "8px") } }) {
                    Span(attrs = { style { property("font-size", "20px") } }) { Text("📺") }
                    Span(attrs = { style { property("font-weight", "700"); property("font-size", "17px") } }) { Text("IPTV Player") }
                }
                NavItem("Home", current == Route.Home) { route = Route.Home }
                NavItem("Browse", current == Route.Browse) { route = Route.Browse }
                NavItem("Favorites", current == Route.Favorites) { route = Route.Favorites }
                NavItem("History", current == Route.History) { route = Route.History }
            }
            Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); property("gap", "12px") } }) {
                if (authState.user?.isAdmin == true) {
                    NavItem("Admin", current == Route.Admin) { route = Route.Admin }
                }
                NavItem("Settings", current == Route.Settings || current == Route.Sources || current == Route.Devices) { route = Route.Settings }
                Span(attrs = { style { property("color", Theme.textDim); property("font-size", "14px") } }) {
                    Text(authState.user?.username ?: "")
                }
                GhostButton("Log out") { authVm.logout() }
            }
        }

        Div(attrs = { style { flexGrow(1) } }) {
            when (current) {
                Route.Home -> HomeScreen(homeVm, onPlay, onSeriesOpen, onOpenSources = { route = Route.Sources })
                Route.Browse -> BrowseScreen(browseVm, onPlay, onSeriesOpen, onOpenSources = { route = Route.Sources })
                is Route.SeriesDetail -> SeriesDetailScreen(
                    sourceId = current.sourceId,
                    seriesId = current.seriesId,
                    seriesName = current.name,
                    onPlayEpisode = onPlayEpisode,
                    onBack = { route = Route.Home },
                )
                Route.Favorites -> FavoritesScreen(favVm, onPlay)
                Route.History -> HistoryScreen(histVm, onPlay)
                Route.Settings -> SettingsScreen(authVm, homeVm, onOpenSources = { route = Route.Sources }, onOpenDevices = { route = Route.Devices })
                Route.Sources -> SourcesScreen(sourceVm, onBack = { route = Route.Settings })
                Route.Devices -> DevicesScreen(deviceVm, onBack = { route = Route.Settings })
                Route.Admin -> {
                    if (authState.user?.isAdmin == true) AdminScreen(remember { koinGet<AdminViewModel>() })
                }
                else -> {}
            }
        }
    }
}

@Composable
private fun NavItem(text: String, active: Boolean, onClick: () -> Unit) {
    Button(attrs = {
        classes("navbtn")
        onClick { onClick() }
        style {
            property("background", if (active) Theme.surfaceAlt else "transparent")
            property("color", if (active) Theme.text else Theme.textDim)
            property("border", "none"); property("border-radius", "8px")
            property("padding", "8px 12px"); property("font-size", "14px")
            property("font-weight", if (active) "600" else "400"); property("cursor", "pointer")
        }
    }) { Text(text) }
}
